import { Space, Spin } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Alert from 'components/new-ui/base/alert/Alert';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import { StyledPageWrapper } from 'components/new-ui/base/wrappers/styles';
import { getDeclaration, mirrorDeclaration } from 'store/declarations/client';
import { Declaration } from 'store/declarations/declaration';
import useProducts from 'hooks/useProducts';
import Notification from 'components/ui/base/notification/Notification';
import IrelandImportH1DeclarationUtils from 'views/declarations/ireland/import/h1/utils';
import GenerationView from './generate-step/GenerationView';
import { ItemErrors, MirroringPreparationLocationState } from './types';
import { StyledButton } from 'views/declarations/declaration-tabs/declaration-form/Form.styles';
import { getSourceDeclarationItems, hasAnyItemErrors, validateIrelandH1Item } from './utils';
import { DeclarationExternalEntity } from 'store/declarations/enums/common/declaration-external-entity';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { CDSExportMessageTypes } from 'store/declarations/enums/common/declaration-types';
import { getDeclarationUrl } from 'views/declarations/utils/getDeclarationUrl';
import { getErrorMessage } from 'utils/getErrorMessage';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import IssuesView from './issues-step/IssuesView';
import ContinueWithErrorsModal from './issues-step/ContinueWithErrorsModal';
import GenerateMultipleMirroredDeclarationsModal from './generate-step/GenerateMultipleMirroredDeclarationsModal';
import { getMirroredDeclarationsCount } from './getMirroredDeclarationsCount';
import { getLandingDeclarationId } from './getLandingDeclarationId';
import { PostMirrorCompletionLocationState } from '../post-mirror-completion/types';

// Which of the page's two steps is on screen. It starts from the item validation outcome, but it is
// not a verdict - the user can move on to 'generate' with issues left unresolved, and come back.
type PreparationStep = 'issues' | 'generate';

// Shown in place of the views when the page has nothing trustworthy to show. Generating is blocked
// alongside them - without the items there is no way to tell the user what would be left empty.
const LOAD_FAILED_MESSAGE = 'We could not load this declaration and its items. Please go back and try again.';
const UNSUPPORTED_SOURCE_MESSAGE = 'Item validation is not available for this type of declaration.';

const MirroringPreparationPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { manifestId, jobId } = useParams();
    const { updateIrelandImportProduct } = useProducts();

    const [isLoadingDeclarationData, setIsLoadingDeclarationData] = useState(true);
    const [loadingMirroringRequest, setLoadingMirroringRequest] = useState(false);
    const [items, setItems] = useState<any[]>([]);
    const [totalItemCount, setTotalItemCount] = useState(0);
    const [itemErrors, setItemErrors] = useState<Record<number, ItemErrors>>({});
    // The errors exactly as validation first reported them. Unlike `itemErrors` these are never
    // updated by a quick fix, so the quick-fix modal can keep showing (and re-editing) the fields
    // an item was originally missing even after they've been filled in.
    const [initialItemErrors, setInitialItemErrors] = useState<Record<number, ItemErrors>>({});
    const [step, setStep] = useState<PreparationStep>('issues');
    const [issuesResolved, setIssuesResolved] = useState(false);
    const [isContinueWithErrorsModalOpen, setIsContinueWithErrorsModalOpen] = useState(false);
    const [isMultipleMirrorsModalOpen, setIsMultipleMirrorsModalOpen] = useState(false);
    const [canReturnToIssuesStep, setCanReturnToIssuesStep] = useState(false);
    const [templateId, setTemplateId] = useState<string | undefined>(undefined);
    const [loadErrorMessage, setLoadErrorMessage] = useState<string | undefined>(undefined);

    const locationState = location?.state as MirroringPreparationLocationState | undefined;
    const title = `${locationState?.mirroredDeclaration?.messageType} Preparation`;
    const mirroredDeclarationsCount = getMirroredDeclarationsCount(totalItemCount);

    const sourceDeclarationUrl = useMemo(() => {
        const sourceDeclarationId = locationState?.sourceDeclaration.declarationId;
        if (!sourceDeclarationId) return undefined;

        return getDeclarationUrl({ manifestId, jobId, declarationId: sourceDeclarationId });
    }, [manifestId, jobId, locationState?.sourceDeclaration.declarationId]);

    useEffect(() => {
        if (!locationState) {
            navigate(-1);
        }
    }, [locationState, navigate]);

    useEffect(() => {
        const sourceDeclaration = locationState?.sourceDeclaration;

        if (!sourceDeclaration?.declarationId) {
            setIsLoadingDeclarationData(false);
            return;
        }

        let isMounted = true;
        setIsLoadingDeclarationData(true);
        setLoadErrorMessage(undefined);

        getDeclaration(sourceDeclaration.declarationId)
            .then(async (declaration: Declaration) => {
                if (!isMounted) return;

                const result = await getSourceDeclarationItems(declaration, sourceDeclaration);
                if (!isMounted) return;

                if (!result) {
                    setItems([]);
                    setTotalItemCount(0);
                    setItemErrors({});
                    setInitialItemErrors({});
                    setLoadErrorMessage(UNSUPPORTED_SOURCE_MESSAGE);
                    return;
                }

                setItems(result.items);
                setTotalItemCount(result.totalItemCount);
                setItemErrors(result.errors);
                setInitialItemErrors(result.errors);
                const hasErrors = Object.keys(result.errors).length > 0;
                setStep(hasErrors ? 'issues' : 'generate');
                setIssuesResolved(!hasErrors);
            })
            .catch((err) => {
                console.error('Failed to fetch source declaration', err);
                if (isMounted) setLoadErrorMessage(LOAD_FAILED_MESSAGE);
            })
            .finally(() => {
                if (isMounted) setIsLoadingDeclarationData(false);
            });

        return () => {
            isMounted = false;
        };
    }, [locationState]);

    const handleItemFixed = async (index: number, rawItem: any) => {
        const declarationId = locationState?.sourceDeclaration.declarationId;
        if (!declarationId) return;

        const savedItem = await updateIrelandImportProduct(rawItem, declarationId, rawItem.id, false);

        if (!savedItem) {
            Notification({
                type: 'error',
                messageTitle: 'Failed to save item',
                description: 'Something went wrong while saving the item. Please try again.',
            });
            throw new Error('Failed to save item');
        }

        const flatItem = IrelandImportH1DeclarationUtils.transformProductForClient(savedItem);
        const newErrors = await validateIrelandH1Item(flatItem);

        const updatedItems = [...items];
        updatedItems[index] = savedItem;
        const updatedErrors = { ...itemErrors, [index]: newErrors };

        setItems(updatedItems);
        setItemErrors(updatedErrors);
        setIssuesResolved(!hasAnyItemErrors(updatedErrors));
    };

    const goToGenerateStep = () => {
        setIsContinueWithErrorsModalOpen(false);
        setCanReturnToIssuesStep(true);
        setStep('generate');
    };

    // Unresolved item errors don't block generation - they only get a confirmation first, so the
    // user knows the mirrored declaration will be created with those fields empty.
    const handleContinue = () => {
        if (issuesResolved) {
            goToGenerateStep();
            return;
        }
        setIsContinueWithErrorsModalOpen(true);
    };

    const handleCancel = () => {
        if (step === 'generate' && canReturnToIssuesStep) {
            setStep('issues');
            return;
        }
        navigate(-1);
    };

    // A source with more items than one mirrored declaration can hold is split, so the user confirms first.
    const handleGenerateClick = () => {
        if (mirroredDeclarationsCount > 1) {
            setIsMultipleMirrorsModalOpen(true);
            return;
        }
        handleGenerateMirroredDeclaration();
    };

    const handleConfirmMultipleMirrors = () => {
        setIsMultipleMirrorsModalOpen(false);
        handleGenerateMirroredDeclaration();
    };

    const handleGenerateMirroredDeclaration = async () => {
        const sourceDeclarationId = locationState?.sourceDeclaration.declarationId;

        if (!locationState || !sourceDeclarationId) {
            console.error('Source declaration id is missing!');
            return;
        }

        try {
            setLoadingMirroringRequest(true);

            const result = await mirrorDeclaration(sourceDeclarationId, {
                declarationExternalEntity: DeclarationExternalEntity.CDS,
                declarationInternalType: DeclarationInternalType.EXPORT,
                declarationName: CDSExportMessageTypes.B1,
                declarationTemplateId: templateId,
            });

            Notification({
                type: 'success',
                messageTitle: 'Mirroring Complete',
                description:
                    mirroredDeclarationsCount > 1
                        ? `Successfully generated ${mirroredDeclarationsCount} mirrored declarations`
                        : 'Successfully mirrored declaration',
            });

            const landingDeclarationId = getLandingDeclarationId(result);
            if (!landingDeclarationId) {
                console.error('Mirrored declaration id is missing!');
                return;
            }

            const postMirrorCompletionState: PostMirrorCompletionLocationState = {
                postMirrorCompletion: {
                    sourceMessageType: locationState.sourceDeclaration.messageType,
                    messageType: locationState.mirroredDeclaration.messageType,
                },
            };

            navigate(getDeclarationUrl({ manifestId, jobId, declarationId: landingDeclarationId }), {
                replace: true,
                state: postMirrorCompletionState,
            });
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            Notification({
                type: 'error',
                messageTitle: 'Mirroring Failed',
                description: errorMessage || DEFAULT_INTERNAL_ERROR_MESSAGE,
            });
        } finally {
            setLoadingMirroringRequest(false);
        }
    };

    if (!locationState) return null;

    return (
        <StyledPageWrapper>
            <PageHeader
                onBackButtonClick={() => navigate(-1)}
                title={title}
                showBackButton
                subtitle="Resolve any issues and optionally select a template to proceed with generation"
                sidebar={
                    <Space>
                        <StyledButton onClick={handleCancel}>Cancel</StyledButton>
                        {!loadErrorMessage && step === 'generate' && (
                            <StyledButton
                                type="primary"
                                loading={loadingMirroringRequest}
                                onClick={handleGenerateClick}
                            >
                                Generate {locationState.mirroredDeclaration.messageType}
                            </StyledButton>
                        )}
                        {!loadErrorMessage && step === 'issues' && (
                            <StyledButton type="primary" onClick={handleContinue}>
                                Continue
                            </StyledButton>
                        )}
                    </Space>
                }
            />

            <div style={{ marginTop: 24 }}>
                {isLoadingDeclarationData && (
                    <div style={{ textAlign: 'center', padding: 48 }}>
                        <Spin size="large" tip="Loading declaration..." />
                    </div>
                )}
                {!isLoadingDeclarationData && loadErrorMessage && (
                    <Alert type="error" showIcon message="Preparation unavailable" description={loadErrorMessage} />
                )}
                {!isLoadingDeclarationData && !loadErrorMessage && step === 'generate' && (
                    <GenerationView
                        country={locationState.mirroredDeclaration.country}
                        internalType={locationState.mirroredDeclaration.internalType}
                        messageType={locationState.mirroredDeclaration.messageType}
                        onTemplateChange={setTemplateId}
                    />
                )}
                {!isLoadingDeclarationData && !loadErrorMessage && step === 'issues' && (
                    <IssuesView
                        country={locationState.sourceDeclaration.country}
                        internalType={locationState.sourceDeclaration.internalType}
                        messageType={locationState.sourceDeclaration.messageType}
                        items={items}
                        errors={itemErrors}
                        initialErrors={initialItemErrors}
                        onResolvedChange={setIssuesResolved}
                        onItemFixed={handleItemFixed}
                        sourceDeclarationUrl={sourceDeclarationUrl}
                    />
                )}
            </div>

            <GenerateMultipleMirroredDeclarationsModal
                open={isMultipleMirrorsModalOpen}
                messageType={locationState.mirroredDeclaration.messageType}
                itemCount={totalItemCount}
                declarationsCount={mirroredDeclarationsCount}
                onConfirm={handleConfirmMultipleMirrors}
                onCancel={() => setIsMultipleMirrorsModalOpen(false)}
            />

            <ContinueWithErrorsModal
                open={isContinueWithErrorsModalOpen}
                messageType={locationState.mirroredDeclaration.messageType}
                onConfirm={goToGenerateStep}
                onCancel={() => setIsContinueWithErrorsModalOpen(false)}
            />
        </StyledPageWrapper>
    );
};

export default MirroringPreparationPage;
